import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../../config';
import ExcelUploadModal from '../ExcelUploadModal';

// Funkce pro formátování čísel s mezerami jako oddělovači tisíců
const formatNumber = (num) => {
  return new Intl.NumberFormat('cs-CZ', {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0
  }).format(num);
};

export default function MainCommissionsTab({ userRole, users, currentUserId, isDarkMode }) {
  const navigate = useNavigate();
  const [commissions, setCommissions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Funkce pro navigaci na detail společnosti podle IČO
  const navigateToCompanyByIco = async (ico) => {
    if (!ico) return;
    
    try {
      console.log('🔍 Vyhledávám společnost s IČO:', ico);
      const response = await fetch(`${API_URL}/companies?search=${ico}&userId=${localStorage.getItem('lastUserId') || ''}`);
      
      if (response.ok) {
        const data = await response.json();
        const companies = data.companies || [];
        
        // Najdi společnost s přesně odpovídajícím IČO
        const company = companies.find(c => c.ico === ico.toString());
        
        if (company) {
          console.log('✅ Nalezena společnost:', company.name);
          navigate(`/companies/${company._id}`);
        } else {
          alert(`Společnost s IČO "${ico}" nebyla nalezena.`);
        }
      } else {
        console.error('Chyba při hledání společnosti:', response.status);
        alert('Chyba při hledání společnosti');
      }
    } catch (error) {
      console.error('Chyba při navigaci na společnost:', error);
      alert('Chyba při otevírání detailu společnosti');
    }
  };

  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [selectedUser, setSelectedUser] = useState(currentUserId);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [editingUserId, setEditingUserId] = useState(null); // Pro sledování, která provize se právě edituje
  const [showManualAddModal, setShowManualAddModal] = useState(false);
  const [manualCommission, setManualCommission] = useState({
    companyName: '',
    ico: '',
    commission: '',
    userId: '',
    month: getCurrentMonth()
  });

  // === Započítání leadů ===
  const [showLeadsModal, setShowLeadsModal] = useState(false);
  const [leadsForm, setLeadsForm] = useState({ batchName: '', month: getCurrentMonth(), pricePerLead: '' });
  const [leadsStats, setLeadsStats] = useState([]);
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [leadsSubmitting, setLeadsSubmitting] = useState(false);
  const [batchOptions, setBatchOptions] = useState([]);

  function getCurrentMonth() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  const fetchCommissions = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/commissions?month=${selectedMonth}&userId=${currentUserId || ''}`;
      if (selectedUser && selectedUser !== 'all') {
        url += `&filterUserId=${selectedUser}`;
      }
      
      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setCommissions(data.commissions || []);
      }
    } catch (error) {
      console.error('Chyba při načítání provizí:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableMonths = async () => {
    try {
      const response = await fetch(`${API_URL}/commissions/months?userId=${currentUserId || ''}`);
      if (response.ok) {
        const data = await response.json();
        setAvailableMonths(data.months || []);
      }
    } catch (error) {
      console.error('Chyba při načítání měsíců:', error);
    }
  };

  const fetchAllUsers = async () => {
    try {
      const response = await fetch(`${API_URL}/commissions/users?userId=${currentUserId || ''}`);
      if (response.ok) {
        const data = await response.json();
        setAllUsers(data.users || []);
      }
    } catch (error) {
      console.error('Chyba při načítání uživatelů:', error);
    }
  };

  const assignUserToCommission = async (commissionId, userId) => {
    try {
      const response = await fetch(`${API_URL}/commissions/${commissionId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId })
      });

      if (response.ok) {
        await fetchCommissions(); // Refresh data
      } else {
        alert('Chyba při přiřazování uživatele');
      }
    } catch (error) {
      console.error('Chyba při přiřazování uživatele:', error);
      alert('Chyba při přiřazování uživatele');
    }
  };

  const deleteMonth = async () => {
    const monthName = new Date(selectedMonth + '-01').toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' });
    const confirmMessage = `Opravdu chcete smazat všechny provize pro měsíc ${monthName}? Tato akce je nevratná!\n\nPočet provizí ke smazání: ${commissions.length}`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/commissions/month/${selectedMonth}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        const data = await response.json();
        alert(`Úspěšně smazáno ${data.deletedCount} provizí pro měsíc ${monthName}`);
        await fetchCommissions();
        await fetchAvailableMonths();
      } else {
        const errorData = await response.json();
        alert('Chyba při mazání: ' + errorData.error);
      }
    } catch (error) {
      alert('Chyba při mazání měsíce');
      console.error('Chyba při mazání měsíce:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteCommission = async (commissionId) => {
    try {
      const response = await fetch(`${API_URL}/commissions/${commissionId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        alert('Provize byla úspěšně smazána');
        fetchCommissions();
      } else {
        const errorData = await response.json();
        alert('Chyba při mazání provize: ' + (errorData.error || 'Neznámá chyba'));
      }
    } catch (error) {
      console.error('Chyba při mazání provize:', error);
      alert('Chyba při mazání provize');
    }
  };

  const payFromRetention = async (commission) => {
    const absAmount = Math.abs(commission.commission || 0);
    if (!window.confirm(`Zaplatit zápornou provizi ${formatNumber(absAmount)} Kč firmy "${commission.companyName}" ze zádržného?\n\nProvize bude vynulována a částka odečtena ze zádržného fondu obchodníka.`)) return;
    try {
      const userId = commission.userId?._id || commission.userId;
      const response = await fetch(`${API_URL}/retention/pay-from-commission`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          commissionId: commission._id,
          commissionType: 'main',
          userId,
          month: selectedMonth
        })
      });
      const data = await response.json();
      if (response.ok) {
        alert(`✅ ${data.message}\nNový stav fondu: ${formatNumber(data.newFundBalance)} Kč`);
        fetchCommissions();
      } else {
        if (data.available !== undefined && data.required !== undefined) {
          alert(`❌ Nedostatek prostředků ve fondu zádržného.\n\nPotřeba: ${formatNumber(data.required)} Kč\nDostupné: ${formatNumber(data.available)} Kč\nChýbí: ${formatNumber(data.required - data.available)} Kč`);
        } else {
          alert('❌ Chyba: ' + (data.error || 'Neznámá chyba'));
        }
      }
    } catch (error) {
      console.error('Chyba při platbě ze zádržného:', error);
      alert('Chyba při platbě ze zádržného');
    }
  };

  const fetchBatches = async () => {
    try {
      const res = await fetch(`${API_URL}/batches`);
      if (res.ok) {
        const data = await res.json();
        setBatchOptions(data); // [{batchName, count}]
      }
    } catch (e) {
      console.error('Chyba při načítání várek:', e);
    }
  };

  const loadLeadsStats = async () => {
    if (!leadsForm.batchName || !leadsForm.month) {
      alert('Vyberte šarži a měsíc');
      return;
    }
    setLeadsLoading(true);
    try {
      const [year, month] = leadsForm.month.split('-');
      const res = await fetch(`${API_URL}/assignment-history?month=${parseInt(month)}&year=${parseInt(year)}`);
      if (res.ok) {
        const data = await res.json(); // [{userId, userName, totalCount, batches:[{batchName, count}]}]
        const filtered = data
          .map(user => {
            const b = (user.batches || []).find(b => b.batchName === leadsForm.batchName);
            if (!b || b.count === 0) return null;
            return { userId: user.userId, userName: user.userName, count: b.count };
          })
          .filter(Boolean)
          .sort((a, b) => b.count - a.count);
        setLeadsStats(filtered);
        if (filtered.length === 0) alert('Pro tuto šarži a měsíc nejsou žádná přidělení.');
      }
    } catch (e) {
      console.error('Chyba při načítání historie přidělování:', e);
      alert('Chyba při načítání dat');
    } finally {
      setLeadsLoading(false);
    }
  };

  const submitLeads = async () => {
    const price = parseFloat(leadsForm.pricePerLead);
    if (!price || price <= 0) {
      alert('Zadejte platnou cenu za kus');
      return;
    }
    if (leadsStats.length === 0) {
      alert('Nejsou žádná data k započítání');
      return;
    }
    const total = leadsStats.reduce((s, u) => s + u.count, 0);
    if (!window.confirm(`Přidat záporné provize za leady:\n${leadsStats.map(u => `${u.userName}: ${u.count} ks × ${price} Kč = ${-(u.count * price)} Kč`).join('\n')}\n\nCelkem: ${total} ks, ${-(total * price)} Kč`)) return;
    setLeadsSubmitting(true);
    try {
      for (const user of leadsStats) {
        const commissionAmount = -(user.count * price);
        const response = await fetch(`${API_URL}/commissions`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            companyName: `LEADY (${user.count} KS)`,
            ico: '00000000',
            bmsl: 0,
            hwBudget: 0,
            hwCoefficient: 0,
            contractLength: 0,
            specialCoefficient: 0,
            gp: 0,
            commission: commissionAmount,
            coefficient: 1,
            userId: user.userId,
            month: leadsForm.month,
            signatureDate: new Date(),
            note: `Automatické započítání leadů ze šarže "${leadsForm.batchName}"`
          })
        });
        if (!response.ok) {
          const err = await response.json();
          throw new Error(err.error || `Chyba pro ${user.userName}`);
        }
      }
      alert(`✅ Leady úspěšně započítány pro ${leadsStats.length} obchodníků`);
      setShowLeadsModal(false);
      setLeadsStats([]);
      setLeadsForm({ batchName: '', month: getCurrentMonth(), pricePerLead: '' });
      fetchCommissions();
    } catch (e) {
      alert('Chyba: ' + e.message);
    } finally {
      setLeadsSubmitting(false);
    }
  };

  const addManualCommission = async () => {
    if (!manualCommission.companyName || !manualCommission.commission || !manualCommission.userId) {
      alert('Vyplňte prosím název firmy, provizi a vyberte uživatele');
      return;
    }

    try {
      // Získej koeficient uživatele
      const coeffResponse = await fetch(`${API_URL}/user/coefficients?userId=${manualCommission.userId}`);
      let coefficient = 1.0;
      if (coeffResponse.ok) {
        const coeffData = await coeffResponse.json();
        coefficient = coeffData.mainCoefficient || 1.0;
      }

      const response = await fetch(`${API_URL}/commissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          companyName: manualCommission.companyName,
          ico: manualCommission.ico || '',
          bmsl: 0,
          hwBudget: 0,
          hwCoefficient: 0,
          contractLength: 0,
          specialCoefficient: 0,
          gp: 0,
          commission: parseFloat(manualCommission.commission),
          coefficient: coefficient,
          userId: manualCommission.userId,
          month: manualCommission.month,
          signatureDate: new Date(),
          note: 'Manuálně přidaná provize adminem'
        })
      });

      if (response.ok) {
        alert('Provize byla úspěšně přidána');
        setShowManualAddModal(false);
        setManualCommission({
          companyName: '',
          ico: '',
          commission: '',
          userId: '',
          month: getCurrentMonth()
        });
        fetchCommissions();
      } else {
        const errorData = await response.json();
        alert('Chyba při přidávání provize: ' + (errorData.error || 'Neznámá chyba'));
      }
    } catch (error) {
      console.error('Chyba při přidávání provize:', error);
      alert('Chyba při přidávání provize');
    }
  };

  useEffect(() => {
    fetchCommissions();
    fetchAvailableMonths();
    fetchAllUsers();
  }, [selectedMonth, selectedUser]);

  // Výpočet souhrnů
  const totalGP = commissions.reduce((sum, c) => sum + (c.gp || 0), 0);
  const totalCommission = commissions.reduce((sum, c) => sum + (c.commission || 0), 0);
  
  // Najdi koeficient z první provize s nenulowym GP (LEADY mají gp=0 a coefficient=1, přeskočit je)
  const displayedCoefficient = commissions.find(c => c.gp && c.gp > 0 && c.coefficient)?.coefficient || null;

  return (
    <div>
      <div className={`mb-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-4 rounded-lg shadow-sm border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
        <div className="flex items-center gap-4">
          <div>
            <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mr-2`}>Měsíc:</label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              className={`border rounded px-3 py-1 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
            >
              <option value={getCurrentMonth()}>Aktuální měsíc</option>
              {[...availableMonths].reverse().map(month => (
                <option key={month} value={month}>
                  {new Date(month + '-01').toLocaleDateString('cs-CZ', { year: 'numeric', month: 'long' })}
                </option>
              ))}
            </select>
          </div>
          {userRole === 'admin' && (
            <div>
              <label className={`text-sm font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-700'} mr-2`}>Uživatel:</label>
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className={`border rounded px-3 py-1 text-sm min-w-[150px] ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
              >
                <option value="">Všichni uživatelé</option>
                {allUsers.map(user => (
                  <option key={user._id} value={user._id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {userRole === 'admin' && (
            <>
              <button
                onClick={() => setShowUploadModal(true)}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 text-sm font-medium"
              >
                📤 Nahrát Excel
              </button>
              <button
                onClick={() => setShowManualAddModal(true)}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm font-medium"
              >
                ➕ Přidat provizi ručně
              </button>
              <button
                onClick={() => { fetchBatches(); setShowLeadsModal(true); }}
                className="bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700 text-sm font-medium"
              >
                📋 Započítání leadů
              </button>
            </>
          )}
          <button
            onClick={fetchCommissions}
            disabled={loading}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm disabled:opacity-50"
          >
            {loading ? 'Načítání...' : 'Obnovit'}
          </button>
          {userRole === 'admin' && commissions.length > 0 && (
            <button
              onClick={deleteMonth}
              disabled={loading}
              className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 text-sm font-medium disabled:opacity-50"
            >
              🗑️ Smazat celý měsíc
            </button>
          )}
        </div>
      </div>

      {/* Souhrny */}
      <div className="grid grid-cols-4 gap-6 mb-6">
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-4 rounded-lg shadow border-l-4 border-blue-500`}>
          <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-1`}>Celkové GP</h3>
          <p className="text-2xl font-bold text-blue-600">{formatNumber(totalGP)} Kč</p>
        </div>
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-4 rounded-lg shadow border-l-4 border-green-500`}>
          <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-1`}>Celkové provize</h3>
          <p className="text-2xl font-bold text-green-600">{formatNumber(totalCommission)} Kč</p>
        </div>
        {selectedUser && (
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-4 rounded-lg shadow border-l-4 border-orange-500`}>
            <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-1`}>Koeficient</h3>
            <p className="text-2xl font-bold text-orange-600">
              {displayedCoefficient ? `${displayedCoefficient}×` : '—'}
            </p>
          </div>
        )}
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} p-4 rounded-lg shadow border-l-4 border-gray-500`}>
          <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'} mb-1`}>Počet záznamů</h3>
          <p className={`text-2xl font-bold ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>{formatNumber(commissions.length)}</p>
        </div>
      </div>

      {/* Tabulka */}
      {loading ? (
        <div className={`text-center py-8 ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>Načítání provizí...</div>
      ) : (
        <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow overflow-hidden`}>
          {commissions.length === 0 ? (
            userRole === 'admin' ? (
              <div className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                Žádné provize pro vybraný měsíc.
                <br />
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="mt-4 text-green-600 hover:text-green-800"
                >
                  📤 Nahrát Excel soubor
                </button>
              </div>
            ) : null
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                  <tr>
                    <th className={`px-3 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} uppercase`}>Název firmy</th>
                    <th className={`px-3 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} uppercase`}>IČO</th>
                    <th className={`px-3 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} uppercase`}>Datum podpisu</th>
                    <th className={`px-3 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} uppercase`}>BMSL</th>
                    <th className={`px-3 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} uppercase`}>HW Koeficient</th>
                    <th className={`px-3 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} uppercase`}>Délka smlouvy</th>
                    <th className={`px-3 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} uppercase`}>Spec. koeficient</th>
                    <th className={`px-3 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} uppercase`}>GP</th>
                    <th className={`px-3 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} uppercase`}>Provize</th>
                    <th className={`px-3 py-3 text-left text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} uppercase`}>Uživatel</th>
                    <th className={`px-3 py-3 text-center text-xs font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} uppercase`}>Akce</th>
                  </tr>
                </thead>
                <tbody className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  {commissions.map((commission) => (
                    <tr key={commission._id} className={`${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}>
                      <td className={`px-3 py-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                        <button
                          onClick={() => navigateToCompanyByIco(commission.ico)}
                          className={`text-left hover:underline ${isDarkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-800'} cursor-pointer`}
                          title={`Otevřít detail společnosti (IČO: ${commission.ico})`}
                        >
                          {commission.companyName}
                        </button>
                      </td>
                      <td className={`px-3 py-2 text-sm text-center font-mono ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>{commission.ico}</td>
                      <td className={`px-3 py-2 text-sm text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                        {commission.signatureDate ? 
                          new Date(commission.signatureDate).toLocaleDateString('cs-CZ') : 
                          '-'
                        }
                      </td>
                      <td className={`px-3 py-2 text-sm text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>{commission.bmsl || 0}</td>
                      <td className={`px-3 py-2 text-sm text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>{commission.hwCoefficient || 0}</td>
                      <td className={`px-3 py-2 text-sm text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>{commission.contractLength || 0}</td>
                      <td className={`px-3 py-2 text-sm text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>{commission.specialCoefficient || 0}</td>
                      <td className={`px-3 py-2 text-sm text-center font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>{commission.gp ? formatNumber(commission.gp) : '0'} Kč</td>
                      <td className={`px-3 py-2 text-sm text-center font-bold ${(commission.commission || 0) < 0 ? 'text-red-600' : 'text-green-600'}`}>{commission.commission ? formatNumber(commission.commission) : '0'} Kč</td>
                      <td className={`px-3 py-2 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                        {userRole === 'admin' ? (
                          editingUserId === commission._id ? (
                            <select 
                              className={`text-sm border rounded px-2 py-1 ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                              onChange={(e) => {
                                if (e.target.value) {
                                  assignUserToCommission(commission._id, e.target.value);
                                  setEditingUserId(null);
                                }
                              }}
                              onBlur={() => setEditingUserId(null)}
                              value={commission.userId || ""}
                              autoFocus
                            >
                              <option value="" disabled>Vyberte uživatele...</option>
                              {allUsers.map(user => (
                                <option key={user._id} value={user._id}>
                                  {user.name}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span 
                              onClick={() => setEditingUserId(commission._id)}
                              className="text-blue-600 cursor-pointer hover:underline"
                            >
                              {commission.userName || commission.userId?.name || 'Vyberte uživatele...'}
                            </span>
                          )
                        ) : (
                          <span className={isDarkMode ? 'text-gray-300' : 'text-gray-900'}>
                            {commission.userName || '-'}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-sm text-center">
                        <div className="flex items-center justify-center gap-2">
                          {(commission.commission || 0) < 0 && (
                            <button
                              onClick={() => payFromRetention(commission)}
                              className="text-yellow-600 hover:text-yellow-800 font-medium text-xs px-2 py-1 rounded border border-yellow-400 hover:bg-yellow-50"
                              title="Zaplatit zápornou provizi ze zádržného fondu obchodníka"
                            >
                              🏦 Ze zádržného
                            </button>
                          )}
                          {userRole === 'admin' && (
                            <button
                              onClick={() => {
                                if (window.confirm(`Opravdu chcete smazat provizi pro firmu "${commission.companyName}"?`)) {
                                  deleteCommission(commission._id);
                                }
                              }}
                              className="text-red-600 hover:text-red-800 font-medium"
                              title="Smazat provizi"
                            >
                              🗑️
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Excel Upload Modal */}
      {showUploadModal && (
        <ExcelUploadModal
          selectedMonth={selectedMonth}
          onClose={() => setShowUploadModal(false)}
          onSuccess={() => {
            setShowUploadModal(false);
            fetchCommissions();
            fetchAvailableMonths();
          }}
        />
      )}

      {/* Manual Add Commission Modal */}
      {showManualAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl max-w-md w-full p-6`}>
            <h3 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              Přidat provizi ručně
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Název firmy *
                </label>
                <input
                  type="text"
                  value={manualCommission.companyName}
                  onChange={(e) => setManualCommission({...manualCommission, companyName: e.target.value})}
                  className={`w-full border rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  placeholder="Např. ABC s.r.o."
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  IČO
                </label>
                <input
                  type="text"
                  value={manualCommission.ico}
                  onChange={(e) => setManualCommission({...manualCommission, ico: e.target.value})}
                  className={`w-full border rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  placeholder="12345678"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Provize (Kč) *
                </label>
                <input
                  type="number"
                  value={manualCommission.commission}
                  onChange={(e) => setManualCommission({...manualCommission, commission: e.target.value})}
                  className={`w-full border rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                  placeholder="Např. 5000"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Uživatel *
                </label>
                <select
                  value={manualCommission.userId}
                  onChange={(e) => setManualCommission({...manualCommission, userId: e.target.value})}
                  className={`w-full border rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                >
                  <option value="">Vyberte uživatele</option>
                  {allUsers.map(user => (
                    <option key={user._id} value={user._id}>
                      {user.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                  Měsíc *
                </label>
                <input
                  type="month"
                  value={manualCommission.month}
                  onChange={(e) => setManualCommission({...manualCommission, month: e.target.value})}
                  className={`w-full border rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowManualAddModal(false);
                  setManualCommission({
                    companyName: '',
                    ico: '',
                    commission: '',
                    userId: '',
                    month: getCurrentMonth()
                  });
                }}
                className={`px-4 py-2 rounded text-sm font-medium ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
              >
                Zrušit
              </button>
              <button
                onClick={addManualCommission}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm font-medium"
              >
                Přidat provizi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Leads Modal — Započítání leadů */}
      {showLeadsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${isDarkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-xl max-w-2xl w-full p-6`}>
            <h3 className={`text-xl font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              📋 Započítání leadů
            </h3>

            {/* Výběr šarže, měsíce a načtení */}
            <div className="flex flex-wrap gap-3 mb-4">
              <div className="flex-1 min-w-[160px]">
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Šarže *</label>
                <select
                  value={leadsForm.batchName}
                  onChange={(e) => { setLeadsForm({ ...leadsForm, batchName: e.target.value }); setLeadsStats([]); }}
                  className={`w-full border rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                >
                  <option value="">Vyberte šarži</option>
                  {batchOptions.map(b => (
                    <option key={b.batchName} value={b.batchName}>{b.batchName}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1 min-w-[160px]">
                <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>Měsíc *</label>
                <input
                  type="month"
                  value={leadsForm.month}
                  onChange={(e) => { setLeadsForm({ ...leadsForm, month: e.target.value }); setLeadsStats([]); }}
                  className={`w-full border rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={loadLeadsStats}
                  disabled={leadsLoading}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 text-sm font-medium disabled:opacity-50"
                >
                  {leadsLoading ? 'Načítání...' : '🔍 Načíst'}
                </button>
              </div>
            </div>
            {/* Tabulka přidělených leadů */}
            {leadsStats.length > 0 && (
              <>
                <div className={`rounded-lg border mb-4 overflow-hidden ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <table className="w-full text-sm">
                    <thead className={isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}>
                      <tr>
                        <th className={`text-left px-4 py-2 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Obchodník</th>
                        <th className={`text-center px-4 py-2 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Počet leadů</th>
                        <th className={`text-right px-4 py-2 font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Srážka</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                      {leadsStats.map((user, i) => {
                        const price = parseFloat(leadsForm.pricePerLead) || 0;
                        return (
                          <tr key={i} className={isDarkMode ? 'bg-gray-800' : 'bg-white'}>
                            <td className={`px-4 py-2 font-medium ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{user.userName}</td>
                            <td className={`px-4 py-2 text-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{user.count} ks</td>
                            <td className={`px-4 py-2 text-right font-semibold text-red-500`}>
                              {price > 0 ? `${formatNumber(-(user.count * price))} Kč` : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className={isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}>
                      <tr>
                        <td className={`px-4 py-2 font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>Celkem</td>
                        <td className={`px-4 py-2 text-center font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>
                          {leadsStats.reduce((s, u) => s + u.count, 0)} ks
                        </td>
                        <td className={`px-4 py-2 text-right font-bold text-red-500`}>
                          {parseFloat(leadsForm.pricePerLead) > 0
                            ? `${formatNumber(-(leadsStats.reduce((s, u) => s + u.count, 0) * parseFloat(leadsForm.pricePerLead)))} Kč`
                            : '—'}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

                <div className="mb-4">
                  <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                    Cena za 1 lead (Kč) *
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="1"
                    value={leadsForm.pricePerLead}
                    onChange={(e) => setLeadsForm({ ...leadsForm, pricePerLead: e.target.value })}
                    className={`w-full border rounded px-3 py-2 text-sm ${isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                    placeholder="Např. 150"
                  />
                </div>
              </>
            )}

            <div className="flex justify-end gap-3 mt-2">
              <button
                onClick={() => { setShowLeadsModal(false); setLeadsStats([]); setLeadsForm({ batchName: '', month: getCurrentMonth(), pricePerLead: '' }); }}
                className={`px-4 py-2 rounded text-sm font-medium ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-800 hover:bg-gray-300'}`}
              >
                Zrušit
              </button>
              {leadsStats.length > 0 && (
                <button
                  onClick={submitLeads}
                  disabled={leadsSubmitting || !leadsForm.pricePerLead}
                  className="bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700 text-sm font-medium disabled:opacity-50"
                >
                  {leadsSubmitting ? 'Přidávání...' : '✅ Přidat leady k provizím'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}